import React, { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import cn from 'classnames'
import Switch from 'react-switch'
import { useDispatch } from 'react-redux'

import { getDocumentTypesCategories, createDocumentType, getDocumentType, updateDocumentType, removeDocumentType } from '../../../api'
import { addSnackbar, dismissSnackbar } from '../../../store/organizationList/actions'
import { useQuery } from '../../../hooks/useQuery'

import { BackIcon } from '../Icons'
import Input from '../../Core/Input/Input'
import InputSelect from '../../Core/InputSelect'
import Label from '../../Core/InputLabel'
import ConfirmationModal from '../../Core/Dialog/ConfirmationModal'

import styles from './styles.module.scss'

const REMINDER_OPTIONS = [
  { id: null, name: 'No reminder' },
  { id: 7, name: '7 days before expiry' },
  { id: 14, name: '14 days before expiry' },
  { id: 30, name: '30 days before expiry' },
  { id: 60, name: '60 days before expiry' },
]

const DUE_DAYS_AFTER_ASSIGNMENT_OPTIONS = [
  { id: 7, name: '7 days' },
  { id: 14, name: '14 days' },
  { id: 30, name: '30 days' },
  { id: 60, name: '60 days' },
]

const OWNERSHIP_OPTIONS = [
  {
    id: 'employee',
    title: 'Employee document',
    description: 'One record per employee, e.g. a licence or contract',
  },
  {
    id: 'company',
    title: 'Company / shared document',
    description: 'One document distributed to many employees',
  },
]

const ACTION_OPTIONS = [
  {
    id: 'none',
    title: 'No action required',
    description: 'Stored for the record; Grownu still logs first and last seen',
  },
  {
    id: 'acknowledgment',
    title: 'Require acknowledgement',
    description: 'I have read and understood this document',
  },
  {
    id: 'approval',
    title: 'Require approval',
    description: 'Explicit approve or decline, without electronic signature',
  },
  {
    id: 'signature',
    title: 'Require electronic signature',
    description: 'Qualified signature through a provider',
  },
]

const DOCUMENT_RULES_OPTIONS = [
  {
    id: false,
    title: 'One active document',
    description: 'A new upload archives the previous one',
  },
  {
    id: true,
    title: 'Multiple documents allowed',
    description: 'Employees can hold several valid documents of this type',
  },
]

const DocumentTypeForm = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const { id: companyId } = useParams()
  const { id: documentTypeId } = useQuery(['id'])

  const headerRef = useRef()
  const contentRef = useRef()
  const confirmationModalRef = useRef()

  const [categories, setCategories] = useState([])
  const [form, setForm] = useState({
    document_type_category_id: null,
    name: '',
    description: '',
    ownership: 'employee',
    employee_upload: true,
    employee_upload_review: false,
    effective_date: true,
    expiration_date: false,
    require_due_date_for_action: false,
    reminder_days: null,
    require_acknowledgment: false,
    require_approval: false,
    require_signature: false,
    track_reading_time: false,
    employee_can_view: true,
    manager_can_view: true,
    suspened_employee_can_view: false,
    multiple_documents: false,
    automatic_assignment: true,
    due_days_after_assignment: 7,
  })
  const [errors, setErrors] = useState({})

  const isEditMode = Boolean(documentTypeId)

  useEffect(() => {
    const rect = contentRef.current.getBoundingClientRect()
    const distance = window.innerHeight - rect.top;
    contentRef.current.style.height = `${distance - 20}px`

    contentRef.current.addEventListener('scroll', () => {
      const scrollTop = contentRef.current.scrollTop
      if (scrollTop > 16) {
        headerRef.current.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
      } else {
        headerRef.current.style.boxShadow = 'none'
      }
    })
  }, [])

  useEffect(() => {
    if (companyId) {
      getDocumentTypesCategories(companyId).then(res => {
        if (res?.success) {
          setCategories(res.data)
        }
      })
    }
  }, [companyId])

  useEffect(() => {
    if (documentTypeId && companyId) {
      getDocumentType(companyId, documentTypeId).then(res => {
        if (res?.success) {
          setForm({
            document_type_category_id: res.data.document_type_category_id,
            name: res.data.name,
            description: res.data.description,
            ownership: res.data.ownership,
            employee_upload: res.data.employee_upload,
            employee_upload_review: res.data.employee_upload_review,
            effective_date: res.data.effective_date,
            expiration_date: res.data.expiration_date,
            reminder_days: res.data.reminder_days,
            require_acknowledgment: res.data.require_acknowledgment,
            require_approval: res.data.require_approval,
            require_signature: res.data.require_signature,
            track_reading_time: res.data.track_reading_time,
            employee_can_view: res.data.employee_can_view,
            manager_can_view: res.data.manager_can_view,
            suspened_employee_can_view: res.data.suspened_employee_can_view,
            multiple_documents: res.data.multiple_documents,
            require_due_date_for_action: res.data.require_due_date_for_action,
            automatic_assignment: res.data.automatic_assignment,
            due_days_after_assignment: res.data.due_days_after_assignment,
          })
        }
      })
    }
  }, [documentTypeId, companyId])

  const handleChange = (name) => (e) => {
    const { value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleToggle = (name) => (checked) => {
    setForm(prev => ({ ...prev, [name]: checked }))
  }

  const getActionValue = () => {
    if (form.require_signature) return 'signature'
    if (form.require_approval) return 'approval'
    if (form.require_acknowledgment) return 'acknowledgment'
    return 'none'
  }

  const handleSelectAction = (value) => {
    setForm(prev => ({
      ...prev,
      require_acknowledgment: value === 'acknowledgment',
      require_approval: value === 'approval',
      require_signature: value === 'signature',
      employee_can_view: value === 'acknowledgment' || value === 'approval' ? true : prev.employee_can_view,
    }))
  }

  const handleChangeOwnership = (ownership) => {
    setForm(prev => ({
      ...prev,
      ownership: ownership,
      require_acknowledgment: ownership === 'employee' ? false : prev.require_acknowledgment,
      require_approval: ownership === 'employee' ? false : prev.require_approval,
      track_reading_time: ownership === 'employee' ? false : prev.track_reading_time,
      employee_can_view: ownership === 'employee' ? true : prev.employee_can_view,
      employee_upload: ownership === 'employee',
      employee_upload_review: ownership === 'company' ? false : prev.employee_upload_review,
    }))
  }

  const handleDelete = async () => {
    const result = await confirmationModalRef.current.show({
      title: t('Delete {{name}}', { name: form.name }),
      description: t('Are you sure you want to delete this type? It will remove all associated documents.'),
    })
    if (result.success) {
      const res = await removeDocumentType(companyId, documentTypeId)
      if (res) {
        handleSuccess(t('Document type deleted successfully'))
      }
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!form.name) newErrors.name = t('Name is required')
    if (!form.document_type_category_id) newErrors.document_type_category_id = t('Category is required')
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCreateDocumentType = async () => {
    if (!validateForm()) {
      return
    }
    const post = {
      name: form.name,
      document_type_category_id: form.document_type_category_id,
      description: form.description,
      ownership: form.ownership,
      employee_upload: form.ownership === 'employee' ? form.employee_upload : false,
      employee_upload_review: form.ownership === 'employee' && form.employee_upload ? form.employee_upload_review : false,
      effective_date: form.effective_date,
      expiration_date: form.expiration_date,
      reminder_days: form.expiration_date ? form.reminder_days : null,
      require_acknowledgment: form.require_acknowledgment,
      require_approval: form.require_approval,
      require_signature: form.require_signature,
      track_reading_time: form.require_acknowledgment ? form.track_reading_time : false,
      employee_can_view: form.employee_can_view,
      manager_can_view: form.manager_can_view,
      suspened_employee_can_view: form.suspened_employee_can_view,
      multiple_documents: form.multiple_documents,
      require_due_date_for_action: form.require_due_date_for_action,
      automatic_assignment: form.automatic_assignment,
      due_days_after_assignment: form.due_days_after_assignment,
    }

    if (isEditMode) {
      const res = await updateDocumentType(companyId, documentTypeId, post)
      if (res?.success) {
        handleSuccess(t('Document type updated successfully'))
      }
      return
    }

    const res = await createDocumentType(companyId, post)
    if (res?.success) {
      handleSuccess(t('Document type created successfully'))
    }
  }

  const handleSuccess = (message) => {
    dispatch(addSnackbar(message, 'success'))
    setTimeout(() => {dispatch(dismissSnackbar())}, 2000)
    history.goBack()
  }

  return (
    <div className={styles.container}>
      <div className={styles.header} ref={headerRef}>
        <div className={styles.backButton} onClick={history.goBack}>
          <BackIcon />
        </div>
        <div>
          <div className={styles.headerTitle}>{isEditMode ? form.name : t('New document type')}</div>
          <div className={styles.headerDesc}>{isEditMode ? form.description : t('Define what this type requires')}</div>
        </div>
        <div className={styles.headerActions}>
          {
            isEditMode
              ? <button type="button" className={cn(styles.button, styles.danger)} onClick={handleDelete}>
                  { t('Delete') }
                </button>
              : null
          }
          <button type="button" className={cn(styles.button, styles.inverted)} onClick={history.goBack}>
            { t('Cancel') }
          </button>
          <button type="button" className={styles.button} onClick={handleCreateDocumentType}>
            { t('Save type') }
          </button>
        </div>
      </div>
      <div className={styles.content} ref={contentRef}>
        <div className={styles.section}>
          <div className={styles.sectionInfo}>
            <div className={styles.sectionTitle}>{ t('General') }</div>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.formItem}>
              <Label text={t('Name')} htmlFor='name' />
              <Input
                id='name'
                name='name'
                type='text'
                placeholder={t('e.g. Safety Instruction')}
                value={form.name}
                onChange={handleChange('name')}
                error={errors.name}
                fullWidth
              />
            </div>
            <div className={styles.formItem}>
              <Label text={t('Description')} htmlFor='description' />
              <textarea
                id='description'
                name='description'
                placeholder={t('Add a description')}
                value={form.description}
                onChange={handleChange('description')}
                rows={4}
                className={styles.textarea}
              />
            </div>
            <div className={styles.formItem}>
              <Label text={t('Category')} htmlFor='document_type_category_id' />
              <InputSelect
                id='document_type_category_id'
                name='document_type_category_id'
                placeholder={t('Select category')}
                options={categories}
                value={form.document_type_category_id}
                onChange={handleChange('document_type_category_id')}
                valueKey='id'
                labelKey='name'
                error={errors.document_type_category_id}
              />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionInfo}>
            <div className={styles.sectionTitle}>{ t('Ownership') }</div>
            <div className={styles.sectionDesc}>{ t('Employee documents belong to one person. Company documents are the same file shared with many employees.') }</div>
          </div>
          <div className={styles.sectionContent}> 
            <div className={styles.cardGrid}>
              {
                OWNERSHIP_OPTIONS.map(option => (
                  <div
                    key={option.id}
                    className={cn(styles.card, { [styles.cardActive]: form.ownership === option.id })}
                    onClick={() => handleChangeOwnership(option.id)}
                  >
                    <div className={styles.cardTitle}>{ t(option.title) }</div>
                    <div className={styles.cardDesc}>{ t(option.description) }</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
        {
          form.ownership === 'employee'
            ? <div className={styles.section}>
                <div className={styles.sectionInfo}>
                  <div className={styles.sectionTitle}>{ t('Upload') }</div>
                  <div className={styles.sectionDesc}>{ t('Who can bring this document into Grownu.') }</div>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.switchRow}>
                    <div className={styles.switchInfo}>
                      <div className={styles.switchTitle}>{ t('Employee can upload') }</div>
                      <div className={styles.switchDesc}>{ t('Employees can add or replace this document from their phone') }</div>
                    </div>
                    <Switch
                      onChange={handleToggle('employee_upload')}
                      checked={!!form.employee_upload}
                      offColor='#DDE2E5'
                      onColor='#0085FF'
                      disabled={true}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      height={22}
                      width={40}
                    />
                  </div>
                  {
                    form.employee_upload
                      ? <div className={styles.switchRow}>
                          <div className={styles.switchInfo}>
                            <div className={styles.switchTitle}>{ t('Manager review required') }</div>
                            <div className={styles.switchDesc}>{ t('Uploads stay in Pending review until an administrator approves them') }</div>
                          </div>
                          <Switch
                            onChange={handleToggle('employee_upload_review')}
                            checked={!!form.employee_upload_review}
                            offColor='#DDE2E5'
                            onColor='#0085FF'
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={22}
                            width={40}
                          />
                        </div>
                      : null
                  }
                </div>
              </div>
            : null
        }
        <div className={styles.section}>
          <div className={styles.sectionInfo}>
            <div className={styles.sectionTitle}>{ t('Dates') }</div>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Effective date') }</div>
                <div className={styles.switchDesc}>{ t('When the document starts to apply') }</div>
              </div>
              <Switch
                onChange={handleToggle('effective_date')}
                checked={!!form.effective_date}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Expiration date required') }</div>
                <div className={styles.switchDesc}>{ t('Documents of this type cannot be saved without an expiry') }</div>
              </div>
              <Switch
                onChange={handleToggle('expiration_date')}
                checked={!!form.expiration_date}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Due date for action required') }</div>
                <div className={styles.switchDesc}>{ t('Documents of this type cannot be saved without a due date for action') }</div>
              </div>
              <Switch
                onChange={handleToggle('require_due_date_for_action')}
                checked={!!form.require_due_date_for_action}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            {
              // (form.expiration_date || form.require_due_date_for_action || true) ? (
              //   <div className={styles.formItem}>
              //     <Label text={t('Expiration reminder')} htmlFor='reminder_days' />
              //     <InputSelect
              //       id='reminder_days'
              //       name='reminder_days'
              //       options={REMINDER_OPTIONS}
              //       value={form.reminder_days}
              //       onChange={handleChange('reminder_days')}
              //       valueKey='id'
              //       labelKey='name'
              //       withoutSearch
              //     />
              //     <div className={styles.fieldHint}>{ t('Creates a reminder event for the employee and their manager.') }</div>
              //   </div>
              // ) : null
            }
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionInfo}>
            <div className={styles.sectionTitle}>{ t('Employee action') }</div>
            <div className={styles.sectionDesc}>{ t('Pick the strongest action this type needs. Seen is always tracked automatically.') }</div>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.cardGrid}>
              {
                ACTION_OPTIONS.map(option => {
                  const isDisabled = form.ownership === 'employee' && ['acknowledgment', 'approval'].includes(option.id)
                  return (
                    <div
                      key={option.id}
                      className={cn(styles.card, {
                        [styles.cardActive]: getActionValue() === option.id,
                        [styles.cardDisabled]: isDisabled
                      })}
                      onClick={() => !isDisabled && handleSelectAction(option.id)}
                    >
                      <div className={styles.cardTitle}>{ t(option.title) }</div>
                      <div className={styles.cardDesc}>{ t(option.description) }</div>
                    </div>
                  )
                })
              }
            </div>
            {
              form.require_acknowledgment
                ? <div className={styles.switchRow}>
                    <div className={styles.switchInfo}>
                      <div className={styles.switchTitle}>{ t('Track reading time') }</div>
                      <div className={styles.switchDesc}>{ t('Show first seen, last seen and time spent in the document') }</div>
                    </div>
                    <Switch
                      onChange={handleToggle('track_reading_time')}
                      checked={form.track_reading_time}
                      disabled={form.ownership === 'employee'}
                      offColor='#DDE2E5'
                      onColor='#0085FF'
                      uncheckedIcon={false}
                      checkedIcon={false}
                      height={22}
                      width={40}
                    />
                  </div>
                : null
            }
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionInfo}>
            <div className={styles.sectionTitle}>{ t('Automatic assignment') }</div>
            <div className={styles.sectionDesc}>{ t('Automatically require this document from new employees so they receive it without manual assignment.') }</div>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Automatic assignment') }</div>
                <div className={styles.switchDesc}>{ t('Automatically require this document from new employees') }</div>
              </div>
              <Switch
                onChange={handleToggle('automatic_assignment')}
                checked={!!form.automatic_assignment}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            {
              form.automatic_assignment ? (
                <div className={styles.formItem}>
                  <Label text={t('Due after assignment')} htmlFor='due_days_after_assignment' />
                  <InputSelect
                    id='due_days_after_assignment'
                    name='due_days_after_assignment'
                    options={DUE_DAYS_AFTER_ASSIGNMENT_OPTIONS}
                    value={form.due_days_after_assignment}
                    onChange={handleChange('due_days_after_assignment')}
                    valueKey='id'
                    labelKey='name'
                    withoutSearch
                  />
                  <div className={styles.fieldHint}>{ t('Employees will be required to provide this document within the selected time after being added to the system.') }</div>
                </div>
              ) : null
            }
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionInfo}>
            <div className={styles.sectionTitle}>{ t('Document rules') }</div>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.cardGrid}>
              {
                DOCUMENT_RULES_OPTIONS.map(option => (
                  <div
                    key={String(option.id)}
                    className={cn(styles.card, { [styles.cardActive]: !!form.multiple_documents === option.id })}
                    onClick={() => setForm(prev => ({ ...prev, multiple_documents: option.id }))}
                  >
                    <div className={styles.cardTitle}>{ t(option.title) }</div>
                    <div className={styles.cardDesc}>{ t(option.description) }</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal ref={confirmationModalRef} />
    </div>
  )
}

export default DocumentTypeForm
